import { Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import { DateTime, IANAZone, Settings } from 'luxon';

/**
 * Zone of the Business Timezone (ADR 0059): business dates and deadlines are Chicago calendar days.
 */
export const BUSINESS_TIMEZONE = 'America/Chicago';

/** The IANA zone the browser runs in. A token so specs can pin it independently of the process. */
export const BROWSER_TIMEZONE = new InjectionToken<string>('BROWSER_TIMEZONE', {
  providedIn: 'root',
  factory: () => Intl.DateTimeFormat().resolvedOptions().timeZone,
});

/** A calendar day as `YYYY-MM-DD`, with no zone attached. */
export type CalendarDate = string;

/** A picked day range; either end may be open. */
export interface DayRange {
  start: CalendarDate | null;
  end: CalendarDate | null;
}

/** Half-open UTC instant range: `after` is inclusive, `before` is exclusive. */
export interface InstantRange {
  after: string | null;
  before: string | null;
}

export type Instant = string | Date;

export interface FormatInstantOptions {
  /** Whether to append the short zone label when the active zone differs from the browser zone. */
  showZoneLabel?: boolean;
  /** Whether to show only the clock time (`HH:mm:ss`), e.g. for a "Draft saved" indicator. */
  timeOnly?: boolean;
}

const LOCALE = 'en-US';
const INSTANT_FORMAT = 'MM/dd/yyyy, h:mm a';
const PLAIN_DATE_FORMAT = 'MM/dd/yyyy';
const TIME_ONLY_FORMAT = 'HH:mm:ss';
const ZONE_LABEL_FORMAT = 'ZZZZ';

/**
 * The User Timezone: the single place that presents instants and converts picked days
 * into UTC instants (ADR 0061). The zone follows the browser unless the user overrides it.
 */
@Injectable({ providedIn: 'root' })
export class UserTimezone {
  readonly browserZone = inject(BROWSER_TIMEZONE);

  private readonly overrideSignal = signal<string | null>(null);

  /** The user's explicit IANA zone override, or `null` when following the browser (Automatic). */
  readonly override = this.overrideSignal.asReadonly();

  /** The active User Timezone: the user's override, else the browser zone. */
  readonly zone = computed(() => this.overrideSignal() ?? this.browserZone);

  constructor() {
    Settings.defaultZone = this.zone();
  }

  /** Sets the user's override; `null` (or a zone this browser does not know) means Automatic. */
  setOverride(zone: string | null): void {
    const knownZone = zone !== null && IANAZone.isValidZone(zone);
    this.overrideSignal.set(knownZone ? zone : null);
    Settings.defaultZone = this.zone();
  }

  /**
   * Formats an instant in the active zone, with a short zone label only when the active zone
   * differs from the browser's (unless suppressed via `options.showZoneLabel = false`).
   */
  formatInstant(instant: Instant | null | undefined, options?: FormatInstantOptions): string {
    const zone = this.zone();
    const dateTime = this.toDateTime(instant);
    if (!dateTime) {
      return '';
    }
    return this.presentInZone(
      dateTime,
      zone,
      options?.showZoneLabel ?? true,
      options?.timeOnly ? TIME_ONLY_FORMAT : INSTANT_FORMAT,
    );
  }

  /**
   * Formats a deadline business date (which ends at Chicago midnight) or an exact deadline instant.
   * In Chicago it stays a plain date; elsewhere it is the exact moment the deadline ends in the
   * active zone.
   */
  formatDeadline(
    deadline: CalendarDate | null | undefined,
    options?: FormatInstantOptions,
  ): string {
    if (!deadline) {
      return '';
    }
    const zone = this.zone();
    const parsed = this.toCalendarDay(deadline, BUSINESS_TIMEZONE);
    if (!parsed) {
      return '';
    }
    if (zone === BUSINESS_TIMEZONE) {
      return parsed.toFormat(PLAIN_DATE_FORMAT);
    }
    const endMoment = deadline.includes('T') ? parsed : parsed.plus({ days: 1 });
    return this.presentInZone(endMoment, zone, options?.showZoneLabel ?? true);
  }

  /**
   * Converts a picked day range in the active zone (or an explicit `zone`) into half-open UTC
   * instants: from the start of the first day (inclusive) to the start of the day after the last
   * day (exclusive).
   */
  dayRangeToInstants(range: DayRange, zone?: string): InstantRange;
  dayRangeToInstants(
    fromDate: CalendarDate | null | undefined,
    toDate: CalendarDate | null | undefined,
    zone?: string,
  ): InstantRange;
  dayRangeToInstants(
    rangeOrFrom: DayRange | CalendarDate | null | undefined,
    zoneOrTo?: string | null,
    maybeZone?: string,
  ): InstantRange {
    const isRangeObject =
      typeof rangeOrFrom === 'object' && rangeOrFrom !== null && 'start' in rangeOrFrom;
    const start = isRangeObject ? rangeOrFrom.start : rangeOrFrom;
    const end = isRangeObject ? rangeOrFrom.end : zoneOrTo;
    const targetZone = (isRangeObject ? zoneOrTo : maybeZone) || this.zone();
    const firstDay = this.toCalendarDay(start, targetZone);
    const lastDay = this.toCalendarDay(end, targetZone);
    return {
      after: firstDay ? this.toUtcIso(firstDay) : null,
      before: lastDay ? this.toUtcIso(lastDay.plus({ days: 1 })) : null,
    };
  }

  /**
   * Converts a Luxon `DateTime`, JS `Date`, or date string into a `YYYY-MM-DD` calendar date in
   * the active zone, returning `''` when absent or invalid.
   */
  toCalendarDate(value: DateTime | Date | string | null | undefined): CalendarDate {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (value instanceof Date) {
      // An invalid Date yields an invalid DateTime, whose toISODate() is null.
      return DateTime.fromJSDate(value, { zone: this.zone() }).toISODate() ?? '';
    }
    return value.isValid ? (value.toISODate() ?? '') : '';
  }

  /** Today's Business Day in Chicago, as `YYYY-MM-DD`, for limits on business-date inputs. */
  chicagoToday(): CalendarDate {
    return DateTime.now().setZone(BUSINESS_TIMEZONE).toISODate() ?? '';
  }

  private presentInZone(
    dateTime: DateTime,
    zone: string,
    showZoneLabel: boolean,
    format: string = INSTANT_FORMAT,
  ): string {
    const local = dateTime.setZone(zone).setLocale(LOCALE);
    const formatted = local.toFormat(format);
    if (!showZoneLabel || zone === this.browserZone) {
      return formatted;
    }
    return `${formatted} ${local.toFormat(ZONE_LABEL_FORMAT)}`;
  }

  private toDateTime(instant: Instant | null | undefined): DateTime | null {
    if (!instant) {
      return null;
    }
    const dateTime =
      instant instanceof Date ? DateTime.fromJSDate(instant) : DateTime.fromISO(instant);
    return dateTime.isValid ? dateTime : null;
  }

  private toCalendarDay(date: CalendarDate | null | undefined, zone: string): DateTime | null {
    if (!date) {
      return null;
    }
    const day = DateTime.fromISO(date, { zone });
    return day.isValid ? day : null;
  }

  private toUtcIso(dateTime: DateTime): string {
    return dateTime.toUTC().toISO({ suppressMilliseconds: true }) ?? '';
  }
}
